import { getDefaultTonic, type ModeLabMode } from '@tunasong/music-lib'
import { standardNoteNames, type NoteName } from '@tunasong/schemas'
import type { ChangeEvent, FC } from 'react'
import styles from './mode-picker.module.css'

const MODES: ModeLabMode[] = ['dorian', 'mixolydian', 'phrygian', 'lydian']

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface ModePickerProps {
  mode: ModeLabMode
  tonic: NoteName
  onChange: (mode: ModeLabMode, tonic: NoteName) => void
}

/**
 * Mode Lab v1 picker (§2 Q2). Segmented buttons for the 4 v1 modes plus a
 * tonic <select> with the 12 standard chromatic tonics. Changing the mode
 * resets the tonic to that mode's canonical default (§8.5 v1 reset rule;
 * per-mode tonic memory deferred to v2).
 */
export const ModePicker: FC<ModePickerProps> = props => {
  const { mode, tonic, onChange } = props
  const handleModeClick = (next: ModeLabMode) => onChange(next, getDefaultTonic(next))
  const handleTonicChange = (e: ChangeEvent<HTMLSelectElement>) =>
    onChange(mode, e.target.value as NoteName)
  return (
    <div className={styles.picker}>
      <div className={styles.modeGroup} role="group" aria-label="Mode">
        {MODES.map(m => (
          <button
            key={m}
            type="button"
            aria-pressed={m === mode}
            onClick={() => handleModeClick(m)}
            className={m === mode ? styles.modeButtonActive : styles.modeButton}
          >
            {capitalize(m)}
          </button>
        ))}
      </div>
      <label className={styles.tonicLabel}>
        Tonic:
        <select
          aria-label="Tonic"
          value={tonic}
          onChange={handleTonicChange}
          className={styles.tonicSelect}
        >
          {standardNoteNames.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
